import { Box, Button, Stack } from '@mui/material'
import { observer } from 'mobx-react'
import React, { FC } from 'react'
import DataUnit from '../../../../../components/customElements/DataUnit/DataUnit'
import CustomAccordion from '../../../../../components/customElements/RequestDataAccordions/utils/CustomAccordion'
import EquipmentCheckingField from '../../../../../components/FormElements/EquipmentCheckingField'
import FieldError from '../../../../../components/FormElements/FieldError'
import FormReceiverAutocomplete from '../../../../../components/FormElements/FormReceiverAutocomplete'
import ReplacedEquipmentCheckingField from '../../../../../components/FormElements/ReplacedEquipmentCheckingField'
import { MaintenanceOrder } from '../../../../../types/service.types'
import { formatSN } from '../../../../../utils/utils/formatSN'
import FormSelect from '../../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormSelect'
import EquipmentStaticData from './EquipmentStaticData'
import { replacementTypes } from './helpers/newChangeEquipment.helpers'
import { useNewChangeEquipment } from './hooks/useNewChangeEquipment'

interface NewEquipmentDataAccProps {
  request?: MaintenanceOrder
  updateCurrentServiceData: (data: MaintenanceOrder) => void
}

const NewEquipmentDataAcc: FC<NewEquipmentDataAccProps> = ({
  request,
  updateCurrentServiceData,
}) => {
  const {
    isEdit,
    handleSubmit,
    onEdit,
    onSubmit,
    control,
    onCheckEquipment,
    canEditSn,
    watch,
    setValue,
    setError,
    canEditReplaced,
    showReplacedFields,
    setLastCheckedId,
    isValid,
    isEquipmentIdChecked,
    isPending,
    onReset,
    disableReplacedFields,
    isReplacedEquipmentIdChecked,
    lastCheckedId,
    lastReplacedCheckedId,
    errors,
    isOnlyEquipment,
    isOnlySn,
    isSnAndEq,
    onCheckReplacedEquipment,
    isEquipmentAllowed,
  } = useNewChangeEquipment(updateCurrentServiceData, request)

  return (
    //@ts-ignore
    <CustomAccordion
      summary={'Данные оборудования'}
      defaultOpenState={true}
      preventHandleOpen={false}
      onEdit={request?.isEquipmentIdEditable ? onEdit : null}
    >
      <Stack
        sx={{ marginTop: '14px', paddingLeft: '12px' }}
        spacing={isEdit ? '14px' : '10px'}
        component={'form'}
        onSubmit={handleSubmit(onSubmit)}
      >
        {isEdit && (
          <>
            <EquipmentCheckingField
              control={control}
              defaultEquipmentId={
                request?.oldEquipmentSerialNumber
                  ? (request?.oldEquipmentSerialNumber ?? '')
                  : (request?.equipmentId ?? '')
              }
              equipmentId={watch('equipmentId')!}
              lastCheckedId={lastCheckedId}
              onChecked={onCheckEquipment}
              error={!!errors.equipmentId}
              hideCheckBtn={!canEditSn}
              withLabel
            />
            {canEditSn && (
              <>
                {watch('hasCas') && (
                  <DataUnit
                    name='S/N оборудования'
                    value={formatSN(watch('receiverSerialNumber'))}
                  />
                )}
                {!watch('hasCas') && (
                  <FormReceiverAutocomplete
                    control={control}
                    name='receiverSerialNumber'
                    fullWidth
                    label='S/N оборудования'
                    setValue={setValue as any}
                    modelName='receiverModelId'
                    setError={setError as any}
                  />
                )}
                {watch('smartCardId') && (
                  <FieldError msg='К данному серийному номеру привязана другая smart карта. Проверьте данные или обратитесь к администратору!' />
                )}
                {watch('hasCas') && (
                  <DataUnit
                    name='Модель оборудования'
                    value={watch('receiverModelId')}
                  />
                )}
                {!watch('hasCas') && (
                  <FormSelect
                    control={control}
                    fullWidth
                    name='receiverModelId'
                    label='Модель оборудования'
                    placeholder=''
                    items={[]}
                    withLabel
                    disabled
                  />
                )}
                {canEditReplaced && (
                  <FormSelect
                    control={control}
                    fullWidth
                    items={replacementTypes}
                    name='replacementType'
                    placeholder=''
                    label='Вариант замены'
                    withLabel
                  />
                )}
                {showReplacedFields && (
                  <>
                    {isSnAndEq && (
                      <>
                        <EquipmentCheckingField
                          control={control}
                          defaultEquipmentId={
                            request?.oldEquipmentSerialNumber
                              ? (request?.equipmentId ?? '')
                              : ''
                          }
                          equipmentId={watch('replacedEquipmentId')!}
                          lastCheckedId={lastReplacedCheckedId}
                          onChecked={onCheckEquipment}
                          error={!!errors.replacedEquipmentId}
                          hideCheckBtn={!watch('replacedEquipmentId')}
                          isReplaced
                          withLabel
                        />
                        {disableReplacedFields && (
                          <DataUnit
                            name='S/N нового оборудования'
                            value={formatSN(
                              watch('replacedReceiverSerialNumber'),
                            )}
                          />
                        )}
                        {!disableReplacedFields && (
                          <FormReceiverAutocomplete
                            control={control}
                            name='replacedReceiverSerialNumber'
                            fullWidth
                            label='S/N нового оборудования'
                            setValue={setValue as any}
                            modelName='replacedReceiverModelId'
                            setError={setError as any}
                            withSetEquipmentId
                            defaultEquipmentId={request?.equipmentId!}
                            setValidId={setLastCheckedId}
                            isReplaced
                          />
                        )}
                        {watch('replacedSmartCardId') && (
                          <FieldError msg='К данному серийному номеру привязана другая smart карта. Проверьте данные или обратитесь к администратору!' />
                        )}
                        {disableReplacedFields && (
                          <DataUnit
                            name='Модель нового оборудования'
                            value={watch('replacedReceiverModelId')}
                          />
                        )}
                        {!disableReplacedFields && (
                          <FormSelect
                            control={control}
                            fullWidth
                            name='replacedReceiverModelId'
                            label='Модель нового оборудования'
                            placeholder=''
                            items={[]}
                            withLabel
                            disabled
                          />
                        )}
                      </>
                    )}
                    {isOnlySn && (
                      <>
                        <FormReceiverAutocomplete
                          control={control}
                          name='replacedReceiverSerialNumber'
                          fullWidth
                          label='S/N нового оборудования'
                          setValue={setValue as any}
                          modelName='replacedReceiverModelId'
                          setError={setError as any}
                          withSetEquipmentId
                          defaultEquipmentId={request?.equipmentId!}
                          setValidId={setLastCheckedId}
                          isReplaced
                        />
                        {watch('replacedSmartCardId') && (
                          <FieldError msg='К данному серийному номеру привязана другая smart карта. Проверьте данные или обратитесь к администратору!' />
                        )}
                        <FormSelect
                          control={control}
                          fullWidth
                          name='receiverModelId'
                          label='Модель оборудования'
                          placeholder=''
                          items={[]}
                          withLabel
                          disabled
                        />
                      </>
                    )}
                    {isOnlyEquipment && (
                      <>
                        <ReplacedEquipmentCheckingField
                          control={control}
                          defaultEquipmentId={
                            request?.oldEquipmentSerialNumber ?? ''
                          }
                          equipmentId={watch('replacedEquipmentId')!}
                          lastCheckedId={lastReplacedCheckedId}
                          onReplacedChecked={onCheckReplacedEquipment}
                          error={!!errors.replacedEquipmentId}
                          hideCheckBtn={!watch('replacedEquipmentId')}
                          withLabel
                        />
                        {!isEquipmentAllowed && (
                          <FieldError msg='Smart карта не доступна. Проверьте данные или обратитесь к администратору!' />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
        {!isEdit && <EquipmentStaticData request={request} />}
        {isEdit && (
          <Box className={'jst-sa'} sx={{ gap: '8px' }}>
            <Button
              type='submit'
              color={'primary'}
              variant={'contained'}
              fullWidth
              disabled={
                !isValid ||
                !isEquipmentIdChecked ||
                !isReplacedEquipmentIdChecked ||
                !!watch('smartCardId') ||
                !!watch('replacedSmartCardId') ||
                !isEquipmentAllowed
              }
              loading={isPending}
            >
              Применить изменения
            </Button>
            <Button
              onClick={onReset}
              color={'primary'}
              variant={'contained'}
              fullWidth
            >
              Отмена
            </Button>
          </Box>
        )}
      </Stack>
    </CustomAccordion>
  )
}

export default observer(NewEquipmentDataAcc)
